import { format, isValid, parseISO } from 'date-fns'

import {
    ehColunaDeTempo,
    nomeDiaSemana,
    nomeMetrica,
    ordenadoPelaConta,
    rotuloCurto,
    rotuloSemValor,
} from './agrupamentoUi'

// Quantas barras deitadas cabem sem virar borrão. O resto continua na tabela e no CSV.
export const MAX_BARRAS = 15
// Eixo de tempo (dia, hora, mês) é barra em pé e fina: cabe um mês inteiro.
export const MAX_BARRAS_EM_PE = 31
// Quantas cores dá para distinguir numa legenda. Com mais valores que isso (70
// lojas), os SERIES_COM_COR maiores ganham cor e o resto vira uma faixa "Outras".
export const MAX_SERIES = 8
export const SERIES_COM_COR = 7
export const ROTULO_OUTRAS = 'Outras'
// Rótulo maior que isso quebra em 3 linhas no eixo e as barras se atropelam.
// Com duas colunas no rótulo ("PRODUTO · LOJA") a barra é mais alta e cabe o dobro.
export const MAX_LETRAS_ROTULO = 36
export const MAX_LETRAS_ROTULO_DUPLO = 72

// Cores do tremor (classes do Tailwind) e o hex de cada uma, para o tooltip e o
// PNG, que não passam pelo Tailwind. "Outras" é sempre cinza.
export const CORES = ['blue', 'emerald', 'amber', 'violet', 'rose', 'cyan', 'orange', 'lime']
export const COR_OUTRAS = 'gray'
export const CORES_HEX = {
    blue: '#3b82f6',
    emerald: '#10b981',
    amber: '#f59e0b',
    violet: '#8b5cf6',
    rose: '#f43f5e',
    cyan: '#06b6d4',
    orange: '#f97316',
    lime: '#84cc16',
    gray: '#9ca3af',
}

// Chave do eixo dentro dos dados do gráfico; as séries entram pelo próprio rótulo.
const CHAVE_ROTULO = 'rotulo'
// Nome inteiro, para o tooltip: no eixo vai a versão curta.
export const CHAVE_ROTULO_COMPLETO = 'rotuloCompleto'

// Rótulo de uma barra. Data vira dd/MM, dia da semana vira Dom/Seg/..., vazio
// vira "Sem cliente" — barra sem nome não diz nada para quem está olhando.
export function formatarRotuloEixo(valor, coluna) {
    if (valor === null || valor === undefined || valor === '') {
        return rotuloSemValor(coluna)
    }

    if (coluna?.nome === 'diaSemana') {
        const dia = nomeDiaSemana(valor)
        if (dia) {
            return dia
        }
    }

    if (coluna?.tipo === 'data') {
        const data = typeof valor === 'string' ? parseISO(valor) : new Date(valor)
        if (isValid(data)) {
            return format(data, 'dd/MM')
        }
    }

    return String(valor)
}

// "PIZZA GRANDE DE CALABRESA COM MUÇARELA…": o nome inteiro fica no tooltip.
export function encurtarRotulo(texto, maximo = MAX_LETRAS_ROTULO) {
    const inteiro = String(texto ?? '')
    if (inteiro.length <= maximo) {
        return inteiro
    }
    return `${inteiro.slice(0, maximo - 1).trimEnd()}…`
}

export function formatarValorGrafico(valor, tipo) {
    const numero = Number(valor)
    const seguro = Number.isFinite(numero) ? numero : 0

    if (tipo === 'dinheiro') {
        return `R$ ${seguro.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`
    }

    return seguro.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
}

// Grupo sem valor válido (soma/média null) vira barra zero de propósito: mandar
// null faz o recharts pular a barra e o rótulo fica órfão no eixo, parecendo bug.
function numeroDaLinha(valor) {
    const numero = Number(valor)
    return Number.isFinite(numero) ? numero : 0
}

function duasCasas(numero) {
    return Math.round(numero * 100) / 100
}

// Frase embaixo do gráfico quando ele não coube inteiro. "Maiores" só quando a
// tabela saiu mesmo ordenada por uma conta, do maior para o menor.
export function avisoCorte(grafico) {
    if (!grafico?.cortado) {
        return ''
    }
    const quantos = grafico.dados?.length || 0
    return `O gráfico mostra só os ${quantos} ${grafico.maiores ? 'maiores' : 'primeiros'}; a tabela e o CSV têm tudo.`
}

function valoresDistintos(linhas, coluna) {
    const vistos = []
    for (const linha of linhas) {
        const nome = formatarRotuloEixo(linha?.[coluna.nome], coluna)
        if (!vistos.includes(nome)) {
            vistos.push(nome)
        }
    }
    return vistos
}

// Com duas colunas, a de menos valores (loja, canal, tipo) vira a cor e a outra
// vira o eixo: "Produto e Loja" e "Loja e Produto" desenham o mesmo gráfico.
// Tempo (dia, data, hora) é sempre o eixo: ninguém lê "uma cor por dia".
function escolherEixoESerie(colunasPor, linhas) {
    const [primeira, segunda] = colunasPor
    if (!segunda) {
        return { eixo: primeira, serie: null, valoresSerie: [] }
    }

    const tempoPrimeira = ehColunaDeTempo(primeira)
    const tempoSegunda = ehColunaDeTempo(segunda)
    // Dia e hora, os dois de tempo: nenhum serve de cor, vão juntos no rótulo.
    if (tempoPrimeira && tempoSegunda) {
        return { eixo: primeira, serie: null, valoresSerie: [] }
    }

    let eixo = primeira
    let serie = segunda
    if (tempoPrimeira !== tempoSegunda) {
        eixo = tempoPrimeira ? primeira : segunda
        serie = eixo === primeira ? segunda : primeira
    } else {
        // A que cabe nas cores vira a cor. Com as duas grandes (18 produtos x 70
        // lojas), fica como a pessoa escolheu: a 2ª vira cor, com "Outras".
        const daSegunda = valoresDistintos(linhas, segunda).length
        const daPrimeira = valoresDistintos(linhas, primeira).length
        if (daSegunda > MAX_SERIES && daPrimeira >= 2 && daPrimeira <= MAX_SERIES) {
            eixo = segunda
            serie = primeira
        }
    }

    const valoresSerie = valoresDistintos(linhas, serie)
    if (valoresSerie.length < 2) {
        return { eixo: primeira, serie: null, valoresSerie: [] }
    }
    return { eixo, serie, valoresSerie }
}

// Eixo de tempo sai em ordem de tempo, seja dia, hora ou data: "os maiores"
// só vale para o eixo de texto.
function compararTempo(a, b) {
    const na = Number(a)
    const nb = Number(b)
    if (Number.isFinite(na) && Number.isFinite(nb)) {
        return na - nb
    }
    return String(a ?? '').localeCompare(String(b ?? ''))
}

// Com mais valores que cores (70 lojas), os 7 de maior total ganham cor e o
// resto é somado numa faixa "Outras" — a legenda continua legível.
function agruparSeriesEmOutras(linhas, serie, valoresSerie, nomePrincipal) {
    if (valoresSerie.length <= MAX_SERIES) {
        return { nomesSeries: valoresSerie, nomeDaSerie: nome => nome, outras: null }
    }

    const totais = new Map(valoresSerie.map(nome => [nome, 0]))
    for (const linha of linhas) {
        const nome = formatarRotuloEixo(linha?.[serie.nome], serie)
        totais.set(nome, (totais.get(nome) || 0) + numeroDaLinha(linha?.[nomePrincipal]))
    }

    const comCor = [...totais.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, SERIES_COM_COR)
        .map(([nome]) => nome)

    return {
        nomesSeries: [...comCor, ROTULO_OUTRAS],
        nomeDaSerie: nome => (comCor.includes(nome) ? nome : ROTULO_OUTRAS),
        outras: {
            coluna: rotuloCurto(serie.label),
            comCor: comCor.length,
            quantas: valoresSerie.length - comCor.length,
        },
    }
}

// Uma barra por valor do eixo, uma cor por valor da série. Soma e contagem
// empilham (a barra inteira é o total do produto); média fica lado a lado.
function pivotar({ linhas, eixo, serie, nomesSeries, nomeDaSerie, nomePrincipal, funcao, maiores, teto }) {
    const eixoDeTempo = ehColunaDeTempo(eixo)
    const grupos = new Map()
    for (const linha of linhas) {
        const completo = formatarRotuloEixo(linha?.[eixo.nome], eixo)
        // Data agrupa pelo valor cru: 04/09 de dois anos são duas barras.
        const chave = eixoDeTempo ? String(linha?.[eixo.nome] ?? '') : completo
        let grupo = grupos.get(chave)
        if (!grupo) {
            grupo = { completo, cru: linha?.[eixo.nome], total: 0, somas: {}, quantos: {} }
            grupos.set(chave, grupo)
        }

        const nome = nomeDaSerie(formatarRotuloEixo(linha?.[serie.nome], serie))
        const valor = numeroDaLinha(linha?.[nomePrincipal])
        grupo.somas[nome] = (grupo.somas[nome] || 0) + valor
        grupo.quantos[nome] = (grupo.quantos[nome] || 0) + 1
        grupo.total += valor
    }

    let lista = [...grupos.values()]
    if (eixoDeTempo) {
        lista.sort((a, b) => compararTempo(a.cru, b.cru))
    } else if (maiores) {
        lista.sort((a, b) => b.total - a.total)
    }
    const cortado = lista.length > teto
    lista = lista.slice(0, teto)

    const dados = lista.map(grupo => {
        const linha = {
            [CHAVE_ROTULO]: encurtarRotulo(grupo.completo),
            [CHAVE_ROTULO_COMPLETO]: grupo.completo,
        }
        nomesSeries.forEach(nome => {
            const soma = grupo.somas[nome] || 0
            // Média só chega aqui com 2 colunas e sem "Outras": cada célula é uma
            // linha da tabela, e o valor é o dela.
            linha[nome] = funcao === 'media' && grupo.quantos[nome]
                ? duasCasas(soma / grupo.quantos[nome])
                : duasCasas(soma)
        })
        return linha
    })

    return { dados, cortado, maiores: maiores && !eixoDeTempo }
}

// Monta o que o gráfico precisa a partir da resposta do relatório. Devolve null
// quando não há gráfico para mostrar (relatório linha a linha ou sem resultado).
// `ordem` é a ordenação que gerou o resultado: diz se o corte é "os maiores".
export function montarDadosGrafico(resultado, ordem) {
    if (!resultado || resultado.agrupado !== true) {
        return null
    }

    const linhas = Array.isArray(resultado.linhas) ? resultado.linhas : []
    const por = resultado.agrupamento?.por || []
    const metricas = resultado.agrupamento?.metricas || []
    if (!linhas.length || !por.length || !metricas.length) {
        return null
    }

    const colunas = Array.isArray(resultado.colunas) ? resultado.colunas : []
    const metaDe = nome => colunas.find(coluna => coluna && coluna.nome === nome) || null

    const colunasPor = por.map(nome => metaDe(nome) || { nome, label: nome, tipo: 'texto' })

    // Uma conta só no gráfico: soma em reais e contagem de pedidos não cabem na
    // mesma escala (R$ 12.000 x 40 pedidos esconderia a segunda barra).
    const principal = metricas[0]
    const nomePrincipal = principal?.nome || nomeMetrica(principal)
    if (!nomePrincipal) {
        return null
    }

    const metaPrincipal = metaDe(nomePrincipal)
    const rotuloPrincipal = metaPrincipal?.label || nomePrincipal
    const tipoValor = metaPrincipal?.tipo || 'numero'
    const funcao = principal?.funcao || String(nomePrincipal).split('_')[0]
    const maiores = ordenadoPelaConta(ordem, metricas)

    const base = {
        indice: CHAVE_ROTULO,
        conta: rotuloPrincipal,
        tipoValor,
        maiores,
        metricasForaDoGrafico: metricas
            .slice(1)
            .map(metrica => metaDe(metrica?.nome || nomeMetrica(metrica))?.label)
            .filter(Boolean),
    }

    const { eixo, serie, valoresSerie } = escolherEixoESerie(colunasPor, linhas)
    // Nome de cliente ou de produto não cabe deitado no eixo de baixo.
    const horizontal = eixo.tipo === 'texto'
    const teto = horizontal ? MAX_BARRAS : MAX_BARRAS_EM_PE

    // Média não se soma: com 3 colunas ou com "Outras" a barra seria média de
    // médias, um número que não está na tabela. Aí cada linha vira uma barra.
    const podePivotar = !!serie
        && !(funcao === 'media' && (colunasPor.length > 2 || valoresSerie.length > MAX_SERIES))

    if (podePivotar) {
        const { nomesSeries, nomeDaSerie, outras } = agruparSeriesEmOutras(linhas, serie, valoresSerie, nomePrincipal)
        const pivot = pivotar({
            linhas, eixo, serie, nomesSeries, nomeDaSerie, nomePrincipal, funcao, maiores, teto,
        })
        const foraDoGrafico = colunasPor
            .filter(coluna => coluna !== eixo && coluna !== serie)
            .map(coluna => rotuloCurto(coluna.label))

        return {
            ...base,
            maiores: pivot.maiores,
            titulo: `${rotuloPrincipal} por ${rotuloCurto(eixo.label)} e ${rotuloCurto(serie.label)}`,
            // O que cada cor é, escrito embaixo do gráfico: a legenda sozinha não diz.
            serie: rotuloCurto(serie.label),
            dados: pivot.dados,
            categorias: nomesSeries,
            cores: nomesSeries.map((nome, indice) => (
                nome === ROTULO_OUTRAS && outras ? COR_OUTRAS : CORES[indice % CORES.length]
            )),
            empilhado: funcao !== 'media',
            horizontal,
            rotuloDuplo: false,
            cortado: pivot.cortado,
            outras,
            colunasForaDoGrafico: foraDoGrafico,
        }
    }

    // Uma barra por linha da tabela: o rótulo junta as colunas do agrupamento.
    const rotuloDuplo = colunasPor.length > 1
    const maximo = rotuloDuplo ? MAX_LETRAS_ROTULO_DUPLO : MAX_LETRAS_ROTULO
    const dados = linhas.slice(0, teto).map(linha => {
        const completo = colunasPor
            .map(coluna => formatarRotuloEixo(linha?.[coluna.nome], coluna))
            .join(' · ')
        return {
            [CHAVE_ROTULO]: encurtarRotulo(completo, maximo),
            [CHAVE_ROTULO_COMPLETO]: completo,
            [rotuloPrincipal]: numeroDaLinha(linha?.[nomePrincipal]),
        }
    })

    return {
        ...base,
        titulo: `${rotuloPrincipal} por ${colunasPor.map(coluna => rotuloCurto(coluna.label)).join(' e ')}`,
        dados,
        categorias: [rotuloPrincipal],
        cores: [CORES[0]],
        empilhado: false,
        serie: null,
        horizontal,
        rotuloDuplo,
        cortado: linhas.length > teto,
        outras: null,
        colunasForaDoGrafico: [],
    }
}

// Frase que explica as cores. Com "Outras", diz também quem ganhou cor própria.
export function avisoCores(grafico) {
    if (!grafico?.serie) {
        return ''
    }
    const outras = grafico.outras
    if (!outras) {
        return `As cores separam por ${grafico.serie}.`
    }
    return `As cores separam por ${grafico.serie}: ${outras.comCor} com maior total ganharam cor própria; as outras ${outras.quantas} estão somadas em "${ROTULO_OUTRAS}".`
}

// Mantida para quem já chama: mesma frase, só quando há "Outras".
export function avisoOutras(grafico) {
    return grafico?.outras ? avisoCores(grafico) : ''
}

// Legenda que vai desenhada no PNG: a do tremor é HTML e não entra no SVG.
export function legendaParaImagem(grafico) {
    if (!grafico || (grafico.categorias || []).length < 2) {
        return []
    }
    return grafico.categorias.map((nome, indice) => ({
        nome,
        cor: CORES_HEX[(grafico.cores || [])[indice]] || CORES_HEX.blue,
    }))
}

// Título que vai escrito no PNG: a imagem sai sem a frase de corte que está na tela.
export function tituloParaImagem(grafico) {
    if (!grafico) {
        return ''
    }
    if (!grafico.cortado) {
        return grafico.titulo
    }
    const quantos = grafico.dados?.length || 0
    return `${grafico.titulo} (${quantos} ${grafico.maiores ? 'maiores' : 'primeiros'})`
}
